import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import { whatsappLink } from "@/constants/site";
import { solucoesHero } from "@/mocks/solucoes";

const STACK = [
  { icon: "ri-layout-masonry-line", label: "Criação de Sites", tone: "primary" },
  { icon: "ri-server-line", label: "Hospedagem & VPS", tone: "accent" },
  { icon: "ri-search-eye-line", label: "SEO & Google", tone: "secondary" },
  { icon: "ri-line-chart-line", label: "Marketing & Ads", tone: "primary" },
];

const TONE: Record<string, string> = {
  primary: "bg-primary-500/15 text-primary-300",
  accent: "bg-accent-500/15 text-accent-300",
  secondary: "bg-secondary-500/15 text-secondary-400",
};

export default function SolucoesHero() {
  return (
    <section className="relative overflow-hidden bg-foreground-950 text-background-50">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=abstract%20dark%20navy%20technology%20composition%20with%20glowing%20blue%20cloud%20servers%2C%20website%20interface%20mockups%2C%20rising%20growth%20charts%20and%20network%20lines%2C%20premium%20digital%20solutions%20visualization%2C%20elegant%20minimal&width=1600&height=900&seq=nx-sol-hero&orientation=landscape"
          alt="Ilustração de soluções digitais da NXHOST: criação de sites, servidores em nuvem e crescimento online"
          title="NXHOST — soluções digitais para empresas"
          fetchPriority="high"
          decoding="async"
          className="h-full w-full object-cover object-top opacity-60"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/95 via-foreground-950/90 to-foreground-950/98"></div>
      <div className="absolute inset-0 nx-grid opacity-40"></div>
      <div className="nx-glow absolute -top-40 right-0 h-[520px] w-[520px] animate-pulseGlow"></div>
      <div className="nx-glow absolute bottom-0 -left-40 h-[420px] w-[420px] animate-pulseGlow"></div>

      <div className="relative mx-auto w-full max-w-7xl px-4 pt-28 pb-16 md:px-8 md:pt-40 md:pb-24">
        <Reveal>
          <nav
            aria-label="Trilha de navegação"
            className="flex flex-wrap items-center gap-2 text-xs text-background-400"
          >
            <Link to="/" className="transition-colors hover:text-background-50">
              Início
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span className="text-background-200">Soluções Digitais</span>
          </nav>
        </Reveal>

        <div className="mt-8 grid grid-cols-1 items-center gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
          <div>
            <Reveal delay={60}>
              <span className="inline-flex items-center gap-2 rounded-full border border-background-50/15 bg-background-50/5 px-4 py-2 text-sm font-medium tracking-wide text-accent-300">
                <i className="ri-flashlight-line text-base" aria-hidden="true"></i>
                {solucoesHero.badge}
              </span>
            </Reveal>

            <Reveal delay={120}>
              <h1 className="mt-6 font-heading text-3xl font-bold leading-[1.12] tracking-tight sm:text-4xl lg:text-[3.25rem]">
                {solucoesHero.title}
              </h1>
            </Reveal>

            <Reveal delay={180}>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-background-200 md:text-lg">
                {solucoesHero.subtitle}
              </p>
            </Reveal>

            <Reveal delay={240}>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link
                  to="/contato"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  Solicitar orçamento
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
                <a
                  href={whatsappLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/20 bg-background-50/5 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
                >
                  <i className="ri-customer-service-2-line text-lg text-secondary-400" aria-hidden="true"></i>
                  Falar com um especialista
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={220}>
            <div className="relative mx-auto w-full max-w-lg lg:max-w-none">
              <div className="rounded-2xl border border-background-50/10 bg-foreground-900/70 p-5 backdrop-blur-sm">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wide text-background-400">
                    Ecossistema NXHOST
                  </span>
                  <span className="flex items-center gap-1.5 text-[11px] font-medium text-secondary-400">
                    <span className="h-2 w-2 rounded-full bg-secondary-400"></span>
                    Online
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  {STACK.map((item) => (
                    <div
                      key={item.label}
                      className="flex items-center gap-3 rounded-lg border border-background-50/10 bg-background-50/5 p-3"
                    >
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${TONE[item.tone]}`}
                      >
                        <i className={`${item.icon} text-lg`} aria-hidden="true"></i>
                      </span>
                      <span className="text-xs font-medium leading-tight text-background-100">
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="mt-4 flex flex-wrap gap-2 border-t border-background-50/10 pt-4">
                  {solucoesHero.highlights.map((item) => (
                    <span
                      key={item}
                      className="inline-flex items-center gap-1.5 rounded-full border border-background-50/10 bg-background-50/5 px-3 py-1 text-[11px] text-background-200"
                    >
                      <i className="ri-check-line text-accent-400" aria-hidden="true"></i>
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              <div className="absolute -bottom-3 -left-3 hidden h-12 w-12 items-center justify-center rounded-xl border border-background-50/10 bg-foreground-900/80 text-accent-300 backdrop-blur-sm sm:flex animate-floaty">
                <i className="ri-rocket-2-line text-2xl" aria-hidden="true"></i>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}